import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteCustomerPrice, getCustomerPrices, updateCustomerPrice } from "../../../../actions/fgModuleActions";
import { getAllLicensesByResellerId } from "../../../../actions/licensesActions";

const formatPrice = (price) => price.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

const CustomerPriceDialog = ({ open, onClose, version }) => {
    const dispatch = useDispatch();
    const { customerPrices, loading } = useSelector((state) => state.fgModules);
    const identity = useSelector((state) => state.identity);
    const versionPrices = customerPrices[version.id];

    const [allLicenses, setAllLicenses] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [priceInput, setPriceInput] = useState("");
    const [newLicense, setNewLicense] = useState(null);
    const [newPrice, setNewPrice] = useState("");
    const [error, setError] = useState({});

    useEffect(() => {
        if (open) {
            if (version) {
                dispatch(getCustomerPrices(version.id));
            }
            dispatch(getAllLicensesByResellerId(identity.user.resellerId)).then((json) => {
                setAllLicenses(json);
            });
        }
    }, [open, version, dispatch, identity.user.resellerId]);

    const handleEdit = (price) => {
        setEditingId(price.licenseId);
        setPriceInput(price.monthlyPrice.toString());
    };

    const handleCancel = () => {
        setEditingId(null);
        setPriceInput("");
    };

    const handleSave = () => {
        if (editingId && !isNaN(priceInput) && Number(priceInput) > 0) {
            dispatch(updateCustomerPrice(editingId, version.id, Number(priceInput)));
            handleCancel();
        }
    };

    const handleDelete = (licenseId) => {
        if (window.confirm("Möchten Sie den individuellen Preis für diese Lizenz wirklich entfernen?")) {
            dispatch(deleteCustomerPrice(licenseId, version.id));
        }
    };

    const handlePriceChange = (e, setter) => {
        const value = e.target.value;
        const regex = /^\d*(\.\d{0,2})?$/;
        if (regex.test(value)) {
            setter(value);
        }
    };

    const handleAddNewPrice = () => {
        if (!newLicense) {
            setError({ newLicense: "Bitte wählen Sie eine Lizenz aus." });
            return;
        }
        if (!newPrice) {
            setError({ newPrice: "Bitte geben Sie einen Preis ein." });
            return;
        }
        if (isNaN(newPrice) || Number(newPrice) <= 0) {
            setError({ newPrice: "Bitte geben Sie einen gültigen Preis ein." });
            return;
        }

        dispatch(updateCustomerPrice(newLicense.LicenseId, version.id, Number(newPrice)));
        setNewLicense(null);
        setNewPrice("");
        setError({});
    };

    const licensesWithCustomPrice = versionPrices?.items.map((p) => p.licenseId) || [];
    const availableLicenses = allLicenses.filter((l) => !licensesWithCustomPrice.includes(l.LicenseId));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Individuelle Lizenzpreise für: {version.version}
                <Typography variant="body2">Standardpreis: {formatPrice(version.monthlyPrice)} €</Typography>
            </DialogTitle>
            <DialogContent>
                <Box component={Paper} sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Neuen individuellen Preis hinzufügen
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Autocomplete
                            options={availableLicenses}
                            getOptionLabel={(option) => `${option.StoreName} (${option.LicenseId})`}
                            value={newLicense}
                            onChange={(event, newValue) => {
                                setNewLicense(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Lizenz auswählen"
                                    size="small"
                                    required
                                    error={!!error.newLicense}
                                    helperText={error.newLicense}
                                />
                            )}
                            sx={{ width: 300 }}
                            isOptionEqualToValue={(option, value) => option.LicenseId === value.LicenseId}
                        />
                        <TextField
                            label="Preis (€)"
                            value={newPrice}
                            onChange={(e) => handlePriceChange(e, setNewPrice)}
                            size="small"
                            type="number"
                            required
                            error={!!error.newPrice}
                            helperText={error.newPrice}
                        />
                        <Button onClick={handleAddNewPrice} variant="contained">
                            Hinzufügen
                        </Button>
                    </Stack>
                </Box>

                <Typography variant="h6">Bestehende individuelle Preise</Typography>
                {loading && !versionPrices ? (
                    <CircularProgress />
                ) : (
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Lizenzname</TableCell>
                                    <TableCell align="right">Effektiver Preis (€)</TableCell>
                                    <TableCell>Zuletzt geändert</TableCell>
                                    <TableCell align="right">Aktionen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {versionPrices?.items.map((price) => (
                                    <TableRow key={price.licenseId}>
                                        <TableCell>{price.licenseName}</TableCell>
                                        <TableCell align="right">
                                            {editingId === price.licenseId ? (
                                                <TextField
                                                    size="small"
                                                    value={priceInput}
                                                    onChange={(e) => handlePriceChange(e, setPriceInput)}
                                                    type="number"
                                                    sx={{ width: "100px" }}
                                                />
                                            ) : (
                                                formatPrice(price.monthlyPrice)
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(price.updatedAt)}</TableCell>
                                        <TableCell align="right">
                                            {editingId === price.licenseId ? (
                                                <>
                                                    <Button onClick={handleSave} size="small">
                                                        Speichern
                                                    </Button>
                                                    <Button onClick={handleCancel} size="small">
                                                        Abbrechen
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton onClick={() => handleEdit(price)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(price.licenseId)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerPriceDialog;
